/**
 * Brandwatch Page
 *
 * Project/query selection, mention sampling, and data source registration.
 */

import React, { useState, useEffect } from 'react';
import { Card, Stack, Flex, Button, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { useBrandwatchProjects, useBrandwatchQueries, useBrandwatchSources, useBrandwatchMutations } from '../../api/brandwatch';
import { useHierarchy } from '../../context/HierarchyContext';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import type { BrandwatchProject, BrandwatchQuery, BrandwatchMention } from '../../api/types';
import { RoleGate } from '../shared/RoleGate';
import { GATES } from '../../auth/roles';
import { errorMessage } from '../../api/errors';

export function BrandwatchPage() {
  const { theme } = useTheme();
  const { selectedProject } = useHierarchy();
  const { data: bwProjects, loading: loadingProjects, error: projectsError } = useBrandwatchProjects();
  const { data: sources, loading: loadingSources, error, refetch } = useBrandwatchSources(selectedProject?.id);
  const { sample, register } = useBrandwatchMutations();

  const [selectedBwProject, setSelectedBwProject] = useState<BrandwatchProject | null>(null);
  const [selectedBwQuery, setSelectedBwQuery] = useState<BrandwatchQuery | null>(null);
  const [hydrateTweets, setHydrateTweets] = useState(false);
  const [mentions, setMentions] = useState<BrandwatchMention[]>([]);
  const [sampling, setSampling] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Hydration is only configurable while the project version is in staging;
  // once past staging the registered source (and its hydration flag) is locked.
  const isStaging = !selectedProject?.status
    || ['staging', 'configured'].includes(selectedProject.status.toLowerCase());

  const { data: bwQueries, loading: loadingQueries } = useBrandwatchQueries(selectedBwProject?.id);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;

  // Reset query selection when project changes
  useEffect(() => {
    setSelectedBwQuery(null);
    setMentions([]);
  }, [selectedBwProject]);

  const handleSample = async () => {
    if (!selectedBwProject || !selectedBwQuery) return;
    setSampling(true);
    setErrorMsg('');
    try {
      // Brandwatch's mentions endpoint requires a start date. Use the project
      // version's configured window, defaulting the start to 90 days ago.
      const toDate = (v?: string | null) => (v ? String(v).slice(0, 10) : undefined);
      const defaultStart = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);
      const result = await sample({
        bw_project_id: selectedBwProject.id,
        bw_query_id: selectedBwQuery.id,
        date_range_start: toDate(selectedProject?.start_ts) || defaultStart,
        date_range_end: toDate(selectedProject?.end_ts),
      });
      if (result) setMentions(result.mentions);
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Failed to sample mentions.'));
    } finally {
      setSampling(false);
    }
  };

  const handleRegister = async () => {
    if (!selectedProject || !selectedBwProject || !selectedBwQuery) return;
    setRegistering(true);
    setErrorMsg('');
    try {
      await register({
        id_project: selectedProject.id,
        bw_project_id: selectedBwProject.id,
        bw_project_name: selectedBwProject.name,
        bw_query_id: selectedBwQuery.id,
        bw_query_name: selectedBwQuery.name,
        hydrate_tweets: hydrateTweets,
      });
      setSelectedBwProject(null);
      setSelectedBwQuery(null);
      setHydrateTweets(false);
      setMentions([]);
      refetch();
    } catch (err) {
      setErrorMsg(errorMessage(err, 'Registration failed.'));
    } finally {
      setRegistering(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: '4px',
    border: `1px solid ${borderColor}`,
    background: theme.colors.background.paper,
    color: textColor,
    fontSize: fontSize.sm,
  };

  if (!selectedProject) {
    return (
      <Stack gap="lg">
        <h1 style={{ ...textStyles.h3, color: textColor }}>Brandwatch</h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                Select a client, matter, and project from the context bar to register Brandwatch sources.
              </span>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  if (loadingSources) return <LoadingState message="Loading Brandwatch sources..." />;
  if (error) return <ErrorDisplay error={error} onRetry={refetch} />;

  return (
    <Stack gap="lg">
      <div>
        <h1 style={{ ...textStyles.h3, color: textColor }}>Brandwatch</h1>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
          Project: {selectedProject.name} (v{selectedProject.version})
        </p>
      </div>

      {/* Source Selection */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="md">
            <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
              Select Data Source
            </span>

            <div>
              <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>
                Brandwatch Project
              </label>
              <select
                style={selectStyle}
                value={selectedBwProject?.id || ''}
                onChange={(e) => {
                  const proj = bwProjects?.find((p) => p.id === Number(e.target.value));
                  setSelectedBwProject(proj || null);
                }}
              >
                <option value="">
                  {loadingProjects ? 'Loading projects...' : '— Select a project —'}
                </option>
                {bwProjects?.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {projectsError && (
                <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.xs, display: 'block', marginTop: '4px' }}>
                  {projectsError.message || 'Failed to load Brandwatch projects.'}
                </span>
              )}
              {!loadingProjects && !projectsError && (bwProjects?.length ?? 0) === 0 && (
                <span style={{ color: mutedColor, fontSize: fontSize.xs, display: 'block', marginTop: '4px' }}>
                  No Brandwatch projects available for this API token.
                </span>
              )}
            </div>

            {selectedBwProject && (
              <div>
                <label style={{ fontSize: fontSize.xs, color: mutedColor, display: 'block', marginBottom: '4px' }}>
                  Query
                </label>
                <select
                  style={selectStyle}
                  value={selectedBwQuery?.id || ''}
                  onChange={(e) => {
                    const q = bwQueries?.find((q) => q.id === Number(e.target.value));
                    setSelectedBwQuery(q || null);
                  }}
                >
                  <option value="">
                    {loadingQueries ? 'Loading queries...' : '— Select a query —'}
                  </option>
                  {bwQueries?.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.name} {q.mention_count != null ? `(${q.mention_count.toLocaleString()} mentions)` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedBwProject && (
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: textColor, fontSize: fontSize.sm, cursor: isStaging ? 'pointer' : 'not-allowed' }}>
                  <input
                    type="checkbox"
                    checked={hydrateTweets}
                    disabled={!isStaging}
                    onChange={(e) => setHydrateTweets(e.target.checked)}
                  />
                  Hydrate tweets
                </label>
                <span style={{ color: mutedColor, fontSize: fontSize.xs, display: 'block', marginTop: '2px' }}>
                  {isStaging
                    ? 'Fetch full tweet content for matched mentions. Locked once the project leaves staging.'
                    : 'Hydration is locked — the project is no longer in staging.'}
                </span>
              </div>
            )}

            <RoleGate minRole={GATES.datasourceWrite}>
              <Flex gap="sm">
                <Button variant="outline" size="sm" onClick={handleSample} disabled={!selectedBwQuery || sampling}>
                  {sampling ? 'Sampling...' : 'Sample Mentions'}
                </Button>
                <Button variant="solid" size="sm" onClick={handleRegister} disabled={!selectedBwQuery || registering || !isStaging}>
                  {registering ? 'Registering...' : 'Register Source'}
                </Button>
              </Flex>
            </RoleGate>

            {errorMsg && (
              <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.sm }}>{errorMsg}</span>
            )}
          </Stack>
        </Card.Body>
      </Card>

      {/* Mention Samples */}
      {mentions.length > 0 && (
        <Card variant="elevated">
          <Card.Body>
            <Stack gap="sm">
              <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm }}>
                Sample Mentions ({mentions.length})
              </span>
              {mentions.map((mention) => (
                <div key={mention.id} style={{ padding: spacing.sm, borderBottom: `1px solid ${borderColor}` }}>
                  <div style={{ fontWeight: 500, color: textColor, fontSize: fontSize.sm }}>{mention.title}</div>
                  <Flex gap="sm" style={{ marginTop: '2px' }}>
                    <span style={{ color: mutedColor, fontSize: fontSize.xs }}>{mention.date}</span>
                    {mention.sentiment && (
                      <span style={{ color: mutedColor, fontSize: fontSize.xs }}>Sentiment: {mention.sentiment}</span>
                    )}
                  </Flex>
                  <p style={{ margin: `${spacing.xs} 0 0`, color: mutedColor, fontSize: fontSize.xs }}>{mention.snippet}</p>
                </div>
              ))}
            </Stack>
          </Card.Body>
        </Card>
      )}

      {/* Registered Sources */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="sm">
            <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
              Registered Sources
            </span>
            {sources && sources.length > 0 ? (
              <div style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', overflow: 'hidden' }}>
                {sources.map((source) => (
                  <div key={source.id} style={{ padding: `${spacing.sm} ${spacing.md}`, borderBottom: `1px solid ${borderColor}` }}>
                    <div style={{ fontWeight: 500, color: textColor, fontSize: fontSize.sm }}>
                      {source.bw_project_name} / {source.bw_query_name}
                    </div>
                    <Flex gap="xs" align="center" style={{ marginTop: '2px' }}>
                      <span style={{
                        fontSize: '0.7rem',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        background: source.active ? theme.colors.semantic.success.light + '30' : theme.colors.background.paper,
                        color: source.active ? theme.colors.semantic.success.default : mutedColor,
                      }}>
                        {source.active ? 'Active' : 'Inactive'}
                      </span>
                      {source.hydrate_tweets && (
                        <span style={{
                          fontSize: '0.7rem',
                          padding: '1px 6px',
                          borderRadius: '3px',
                          background: `${theme.colors.primary[600]}15`,
                          color: theme.colors.primary[600],
                        }}>
                          Hydrate tweets
                        </span>
                      )}
                    </Flex>
                  </div>
                ))}
              </div>
            ) : (
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>No sources registered yet.</span>
            )}
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}